import { useEffect, useRef, useState } from "react";
import { Purchases } from "@revenuecat/purchases-capacitor";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import BackButton from "@/components/payments/BackButton";
import SelectRevenueCatPlanCard from "@/components/payments/SelectRevenueCatPlanCard";
import Spinner from "@/components/Spinner";
import EmptyState from "@/components/EmptyState";
import { CloudOffIcon } from "lucide-react";
import { useGetAccountSettings } from "@/tanstack-hooks";
import { useTranslation } from "@/hooks/useTranslation";

// Module-level guard so the SDK is configured only once per app session,
// not every time the user navigates to /upgrade.
let isConfigured = false;

export default function UpgradePageIAP() {
  const apiKey = import.meta.env.VITE_REVENUECAT_IOS_KEY;
  const isWide = useMediaQuery("(min-width: 768px)");
  const accountSettingsQuery = useGetAccountSettings();
  const email = accountSettingsQuery.data?.email;
  const t = useTranslation();

  const [isReady, setIsReady] = useState(false);
  const [initFailed, setInitFailed] = useState(false);
  const lastIdentifiedRef = useRef<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!apiKey) {
      console.error("RevenueCat API key is not set");
      setInitFailed(true);
      return;
    }
    if (!email) return;

    let cancelled = false;
    const init = async () => {
      try {
        if (!isConfigured) {
          await Purchases.configure({ apiKey });
          isConfigured = true;
        }
        if (lastIdentifiedRef.current !== email) {
          await Purchases.logIn({ appUserID: email });
          lastIdentifiedRef.current = email;
        }
        if (!cancelled) setIsReady(true);
      } catch (err) {
        console.error("[RevenueCat] init failed:", err);
        if (!cancelled) setInitFailed(true);
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [apiKey, email]);

  return (
    <div
      className={`flex flex-col px-2 pb-6 ${
        isWide
          ? "w-full max-w-5xl mx-auto mt-2 items-center"
          : "fixed inset-0 bg-black pt-[calc(env(safe-area-inset-top)+1rem)] pb-[calc(env(safe-area-inset-bottom)+1.5rem)] overflow-scroll"
      }`}
    >
      <div className="w-full flex justify-start mb-4">
        <BackButton />
      </div>

      <div className="w-full flex flex-col items-center">
        {initFailed ? (
          <EmptyState
            icon={CloudOffIcon}
            title={t.SELECT_PLAN_CARD.ERRORS.LOADING}
            message={t.SHARED.ERRORS.PERSISTENT}
          />
        ) : !isReady ? (
          <div className="flex items-center justify-center w-full h-[60vh]">
            <Spinner />
          </div>
        ) : (
          <SelectRevenueCatPlanCard />
        )}
      </div>
    </div>
  );
}
